import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FTC_DISCLOSURE } from '../../utils/helpers';
import api from '../../services/api';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | subscribed | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      await api.post('/newsletter/subscribe', { email, source: 'footer' });
      setStatus('subscribed');
      setEmail('');
      setTimeout(() => setStatus('idle'), 4000);
    } catch (err) {
      const msg = err.response?.data?.errors?.email?.[0]
        || err.response?.data?.message
        || 'Couldn’t subscribe — please try again.';
      setErrorMsg(msg);
      setStatus('error');
    }
  };

  const buttonLabel =
    status === 'submitting' ? 'Subscribing…'
    : status === 'subscribed' ? '✓ Subscribed'
    : 'Subscribe';

  return (
    <footer style={{ marginTop: 'auto' }}>
      {/* Newsletter strip */}
      <div className="footer-newsletter">
        {/* auto-fit + minmax so the two halves stack instead of being forced
            side by side — a hard '1fr 1fr' pushed this strip to 337px inside a
            320px viewport, taking the Subscribe button off-screen. */}
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 32, alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 6, color: '#fff', letterSpacing: '-0.02em' }}>Never miss a perk.</h3>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>Get the week's best deals, flash drops, and cashback alerts — straight to your inbox.</p>
          </div>
          <div>
            <form onSubmit={handleSubscribe} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input
                type="email" required value={email} onChange={e => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
                placeholder="Enter your email" disabled={status === 'submitting'}
                // minWidth:0 is the important one — a flex item defaults to
                // min-width:auto, so the input refused to shrink below its
                // intrinsic width and shoved the button past the screen edge.
                style={{ flex: '1 1 180px', minWidth: 0, padding: '14px 18px', borderRadius: 999, border: 'none', fontSize: 14, outline: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
              />
              <button type="submit" className="btn btn-dark" disabled={status === 'submitting'}
                style={{ padding: '14px 26px', borderRadius: 999, fontSize: 14, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {buttonLabel}
              </button>
            </form>
            {status === 'error' && (
              <p style={{ marginTop: 8, fontSize: 12, color: '#FCA5A5' }}>{errorMsg}</p>
            )}
            {status === 'subscribed' && (
              <p style={{ marginTop: 8, fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
                Thanks — check your inbox for a welcome note.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div style={{ background: '#0E0F2E', color: '#9aa5b4' }}>
        <div className="container" style={{ padding: '64px 20px 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr 1fr', gap: 40, marginBottom: 48 }} className="footer-grid">
            <div>
              <Link to="/" style={{ display: 'block', marginBottom: 18 }}>
                <img src="/images/logo-white.svg" alt="PerkVora"
                  style={{ height: 36, width: 'auto', display: 'block' }}
                  onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }}
                />
                <span style={{ display: 'none', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', fontWeight: 800, fontSize: 24, letterSpacing: '-0.02em', color: '#fff' }}>
                  Perk<span style={{ background: 'var(--brand-gradient-text)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Vora</span>
                </span>
              </Link>
              <p style={{ fontSize: 13, lineHeight: 1.7, maxWidth: 280, marginBottom: 20 }}>
                Perks that value you. The all-in-one platform for exclusive deals, cashback, and the brands you love.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <Social href="https://twitter.com" label="X (Twitter)"><XIcon /></Social>
                <Social href="https://instagram.com" label="Instagram"><InstagramIcon /></Social>
                <Social href="https://facebook.com" label="Facebook"><FacebookIcon /></Social>
                <Social href="https://tiktok.com" label="TikTok"><TikTokIcon /></Social>
                <Social href="https://linkedin.com" label="LinkedIn"><LinkedInIcon /></Social>
              </div>
            </div>

            <div>
              <h4 style={footerHead}>Deals</h4>
              <FooterLink to="/coupons">All Coupons</FooterLink>
              <FooterLink to="/coupons?featured=true">Featured Deals</FooterLink>
              <FooterLink to="/coupons?discount_type=percent">Percent Off</FooterLink>
              <FooterLink to="/coupons?discount_type=freebie">Free Stuff</FooterLink>
              <FooterLink to="/merchants">All Brands</FooterLink>
            </div>

            <div>
              <h4 style={footerHead}>Company</h4>
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/how-it-works">How It Works</FooterLink>
              <FooterLink to="/blog">Blog</FooterLink>
              <FooterLink to="/careers">Careers</FooterLink>
              <FooterLink to="/press">Press</FooterLink>
            </div>

            <div>
              <h4 style={footerHead}>For Business</h4>
              <FooterLink to="/for-merchants">List Your Business</FooterLink>
              <FooterLink to="/pricing">Pricing</FooterLink>
              <FooterLink to="/partners">Partner API</FooterLink>
              <FooterLink to="/affiliate">Affiliate Program</FooterLink>
              <FooterLink to="/contact">Contact Sales</FooterLink>
            </div>

            <div>
              <h4 style={footerHead}>Support</h4>
              <FooterLink to="/faq">FAQ</FooterLink>
              <FooterLink to="/contact">Help Center</FooterLink>
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
              <FooterLink to="/terms">Terms of Service</FooterLink>
              <FooterLink to="/ccpa">CA Privacy Rights</FooterLink>
              <FooterLink to="/disclosure">Affiliate Disclosure</FooterLink>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #2d3452', paddingTop: 24, display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontSize: 12 }}>© {new Date().getFullYear()} PerkVora. All rights reserved.</p>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#6b7299' }}>🇺🇸 United States · English</span>
              <span style={{ fontSize: 12, fontStyle: 'italic', color: '#6b7299' }}>{FTC_DISCLOSURE}</span>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 960px) {
            .footer-grid { grid-template-columns: 1fr 1fr 1fr !important; }
          }
          @media (max-width: 720px) {
            .footer-newsletter .container { grid-template-columns: 1fr !important; }
            .footer-grid { grid-template-columns: 1fr 1fr !important; }
          }
          @media (max-width: 480px) {
            .footer-grid { grid-template-columns: 1fr !important; }
            /* One column on phones: centre it, so the link lists do not hug
               the left edge of an otherwise symmetric page (QA 09-18 #2). */
            .footer-grid > div { text-align: center; }
            .footer-grid img { margin-left: auto; margin-right: auto; }
            .footer-grid p { margin-left: auto; margin-right: auto; }
            .footer-grid > div:first-child > div { justify-content: center; }
          }
        `}</style>
      </div>
    </footer>
  );
}

const footerHead = { color: '#fff', fontSize: 12, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.1em' };

function FooterLink({ to, children }) {
  return (
    <Link to={to} style={{ display: 'block', color: '#9aa5b4', fontSize: 13, marginBottom: 10, textDecoration: 'none', transition: 'color 0.15s' }}
      onMouseEnter={e => e.target.style.color = '#fff'}
      onMouseLeave={e => e.target.style.color = '#9aa5b4'}
    >{children}</Link>
  );
}

function Social({ href, label, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
      style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', textDecoration: 'none', transition: 'all 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--brand-gradient)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
    >{children}</a>
  );
}

/* Inline brand glyphs at 16px (currentColor → inherits white from the link).
   Sourced from official brand guidelines; the X logo replaces the old
   Twitter bird per the 2023 rebrand, Instagram uses the camera-lens mark,
   TikTok uses its quaver glyph. */
function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function InstagramIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}
function FacebookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
    </svg>
  );
}
function TikTokIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.83 20.1a6.34 6.34 0 0 0 10.86-4.43V8.66a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.87-.09z" />
    </svg>
  );
}
function LinkedInIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}
